import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { LoadingIcon } from '@/components/LoadingIcon'
import { LoginHeader, PoweredByLabel } from '@/components/widget-components'
import { useAuth } from '@/contexts/auth-context'
import { useOnboardingRequirements } from '@/hooks/useOnboardingRequirements'
import { useWidgetConfig } from '@/hooks/useWidgetConfig'
import { Stepper } from '@/pages/Signup/Stepper/Stepper'
import {
  trackOnboardingCompleted,
  trackOnboardingStepViewed,
} from '@/utils/analytics'
import { DarkModeMenu } from './DarkmodeSignUpPanel'

const POLL_INTERVAL_MS = 3000
const SUCCESS_DISPLAY_MS = 1500

type View = 'waiting' | 'success' | 'processing'

export const CompleteRegistrationStandard = () => {
  const { t } = useTranslation()
  const { isAuth, loading: authLoading } = useAuth()
  const { data: widgetConfig, isPending: isWidgetConfigPending } =
    useWidgetConfig()
  const redirectURL = widgetConfig?.completionRedirectUrl

  const { data: requirements } = useOnboardingRequirements(isAuth, {
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return POLL_INTERVAL_MS
      if (data.satisfied.includes('kyc')) return false
      if (data.processing.includes('kyc')) return false
      return POLL_INTERVAL_MS
    },
  })

  const view: View = (() => {
    if (!requirements) return 'waiting'
    if (requirements.satisfied.includes('kyc')) return 'success'
    if (requirements.processing.includes('kyc')) return 'processing'
    return 'waiting'
  })()

  useEffect(() => {
    trackOnboardingStepViewed('registration_complete')
  }, [])

  useEffect(() => {
    if (view !== 'success' || isWidgetConfigPending) return
    trackOnboardingCompleted()
    const timer = window.setTimeout(() => {
      window.location.href = redirectURL || '/'
    }, SUCCESS_DISPLAY_MS)
    return () => window.clearTimeout(timer)
  }, [view, isWidgetConfigPending, redirectURL])

  const isIframe = window.self !== window.top

  if (authLoading) {
    return (
      <div
        className="my-auto flex justify-center"
        data-testid="container-complete-auth-loading"
      >
        <LoadingIcon />
      </div>
    )
  }

  if (!isAuth) {
    return <Navigate to="/EasyLogin" replace />
  }

  const heading = (() => {
    switch (view) {
      case 'waiting':
        return t('completeRegistrationStandard.thankYou')
      case 'success':
        return t('completeRegistrationStandard.successHeading')
      case 'processing':
        return t('completeRegistrationStandard.thankYou')
    }
  })()

  const message = (() => {
    switch (view) {
      case 'waiting':
        return t('completeRegistrationStandard.waitingMessage')
      case 'success':
        return t('completeRegistrationStandard.successMessage')
      case 'processing':
        return t('completeRegistrationStandard.pendingMessage')
    }
  })()

  return (
    <div
      className="flex flex-col md:flex-row"
      data-testid="container-registration-standard-container"
    >
      <LoginHeader>
        <Stepper />
      </LoginHeader>
      <div className="relative p-5 lg:py-20 w-full">
        {window.self === window.top && (
          <div
            className="absolute top-4 right-0 md:top-10 md:right-0"
            data-testid="container-menu-container"
          >
            <DarkModeMenu />
          </div>
        )}
        <div className="flex-grow mb-5">
          <div
            className="flex flex-col gap-5"
            data-testid={`container-complete-${view}`}
          >
            <h2
              className="text-lg font-normal uppercase mt-4"
              data-testid="text-thank-you-heading"
            >
              {heading}
            </h2>
            <br />
            <div className="mb-16" data-testid="text-status-message">
              {message}
            </div>
            {view === 'waiting' && (
              <div
                className="flex justify-center"
                data-testid="container-complete-loading"
              >
                <LoadingIcon />
              </div>
            )}
            {view !== 'success' && (
              <p
                className="mt-6 text-sm text-center"
                data-testid="text-complete-support"
              >
                {t('support.needHelp')}{' '}
                <a
                  href={`mailto:support@paytrie.com?subject=${encodeURIComponent(
                    t('completeRegistrationStandard.supportSubject')
                  )}`}
                  className="underline"
                  data-testid="link-complete-support"
                >
                  support@paytrie.com
                </a>
              </p>
            )}
          </div>
        </div>

        <div
          className="absolute left-4 right-0 flex justify-center p-2"
          style={{
            top: isIframe
              ? window.innerWidth < 640
                ? '534px'
                : '534px'
              : window.innerWidth < 640
                ? '534px'
                : '660px',
          }}
          data-testid="container-by-container"
        >
          <PoweredByLabel />
        </div>
      </div>
    </div>
  )
}
